import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import ImageLoader from '../components/ImageLoader';
import BuyButton from '../components/BuyButton';
import DoartiCta from '../components/DoartiCta';
import ShippingInfo from '../components/ShippingInfo';
import { getPrintForPainting } from '../data/prints';
import '../components/ImageLoader.css';
import './Gallery.css';
import './PaintingDetail.css';

// Utility function to truncate text to 100 characters and clean paragraph spacing
const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  // Replace multiple newlines and spaces with a single space
  const cleanedText = text.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  if (cleanedText.length <= maxLength) return cleanedText;
  return cleanedText.substring(0, maxLength).trim() + '...';
};

const PaintingDetail = () => {
  const { year, slug } = useParams();
  const { t, translations } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);

  // This would typically come from an API or more detailed data structure
  const getPaintingData = (year, slug) => {
    if (!translations || Object.keys(translations).length === 0) {
      return null;
    }
    
    const galleries = {
      '2022': translations.gallery,
      '2023': translations.gallery2023,
      '2024': translations.gallery2024,
      '2025': translations.gallery2025,
      '2026': translations.gallery2026
    };
    
    const gallery = galleries[year];
    if (!gallery || !gallery.paintings) return null;
    
    return gallery.paintings.find(painting => 
      painting.link === `${year}/${slug}`
    );
  };

  const painting = getPaintingData(year, slug);
  const matchingPrint = getPrintForPainting(year, slug);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setZoomLevel(100);
  };

  useEffect(() => {
    if (!lightboxOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('painting-lightbox-open');

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setLightboxOpen(false);
        setZoomLevel(100);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('painting-lightbox-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [lightboxOpen]);

  // Show loading state if translations are not loaded
  if (!translations || Object.keys(translations).length === 0) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading painting details...</p>
      </div>
    );
  }

  if (!painting) {
    console.log('Painting not found for:', { year, slug, translations: Object.keys(translations) });
    return (
      <div className="container text-center py-5">
        <h2>Painting not found</h2>
        <p>Year: {year}, Slug: {slug}</p>
        <DoartiCta to="/" icon="bi-house-door">
          Back to Home
        </DoartiCta>
      </div>
    );
  }

  // Get related paintings for "You can also like" section
  const getRelatedPaintings = () => {
    if (!translations || Object.keys(translations).length === 0) return [];

    const galleries = {
      '2022': translations.gallery,
      '2023': translations.gallery2023,
      '2024': translations.gallery2024,
      '2025': translations.gallery2025,
      '2026': translations.gallery2026,
    };

    const allPaintings = [];
    Object.values(galleries).forEach((gallery) => {
      if (gallery && gallery.paintings) {
        allPaintings.push(...gallery.paintings);
      }
    });

    // Filter out current painting, shuffle, and return up to 3 random paintings
    const filteredPaintings = allPaintings.filter((p) => p.link !== `${year}/${slug}`);

    // Shuffle array and take first 3
    const shuffled = filteredPaintings.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  };

  const relatedPaintings = getRelatedPaintings();

  // Get painting title
  const getPaintingTitle = () => {
    const headingKey = `${slug}_heading`;
    let heading = t(headingKey);
    // If translation returns the key itself, it wasn't found - use fallback
    if (heading === headingKey) {
      // Try to get title from painting data, or translate it if it's a key
      const title = painting?.title;
      if (title && title.includes('_heading')) {
        // Title is a translation key reference, try to translate it
        heading = t(title);
        if (heading === title) {
          return title; // Return the key if translation still not found
        }
      }
      return title || 'Painting';
    }
    return heading;
  };

  // Get painting description
  const getPaintingDescription = () => {
    const descKey = `${slug}_description`;
    let description = t(descKey);
    // If translation returns the key itself, it wasn't found - use fallback
    if (description === descKey) {
      // Try to get description from painting data, or translate it if it's a key
      const desc = painting?.description;
      if (desc && desc.includes('_description')) {
        // Description is a translation key reference, try to translate it
        description = t(desc);
        if (description === desc) {
          return desc; // Return the key if translation still not found
        }
      }
      return desc || '';
    }
    return description;
  };

  return (
      <>
      <header
        className="site-header"
        id="header-solid"
        aria-hidden="true"
      />
      <section className="latest-podcast-section section-padding painting-detail-section pb-0" id="section_2">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-12">
                <div className="row">
                  <div className="col-lg-6 col-12">
                        <div className="custom-block-icon-wrap">
                          <div className="custom-block-image-wrap custom-block-image-detail-page painting-image-container">
                            <ImageLoader
                              src={`/${painting.image}`}
                              alt={painting.title}
                              className="custom-block-image img-fluid painting-main-image"
                              natural
                            />
                            <button 
                              className="painting-view-fullsize-btn"
                              onClick={() => setLightboxOpen(true)}
                              aria-label="View full size"
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                              </svg>
                            </button>
                          </div>
                      <span
                        className={`status-tag position-absolute ${
                          painting.sold ? 'status-tag--sold' : 'status-tag--available'
                        }`}
                      >
                        {painting.sold ? t('sold') : t('available')}
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12">
                    <div className="custom-block-info">
                      <div className="page-heading">
                        <h1 className="page-heading__title">{getPaintingTitle()}</h1>
                      </div>

                      <div className="painting-detail-tags">
                        <span className="painting-detail-tag painting-detail-tag--medium">
                          <i className="bi bi-palette2" aria-hidden="true"></i>
                          {t('oil_painting')}
                        </span>
                        <span className="painting-detail-tag painting-detail-tag--size">
                          <i className="bi bi-aspect-ratio" aria-hidden="true"></i>
                          {painting.dimensions}
                        </span>
                        <span className="painting-detail-tag painting-detail-tag--year">
                          <i className="bi bi-calendar3" aria-hidden="true"></i>
                          {painting.year || year}
                        </span>
                      </div>

                      {painting.framed && (
                        <div className="mb-3">
                          <span className="meta-tag meta-tag--framed">{t('framed')}</span>
                        </div>
                      )}

                      <p style={{whiteSpace: 'pre-line', marginTop: '1.5rem'}}>
                        {getPaintingDescription()}
                      </p>

                      {painting.recreatedBy && (
                        <div className="mb-3 mt-3">
                          <span className="badge badge--muted">
                            {t(`created_by_${painting.recreatedBy}`)}
                          </span>
                        </div>
                      )}

                      <div className="modern-price-cta-section mt-5">
                        {painting.sold ? (
                            <div className="price-card sold-card">
                              <div className="price-label">{t('sold')}</div>
                              <div className="price-subtitle">{t('not_available')}</div>
                          </div>
                        ) : painting.externalBuyUrl ? (
                            <div className="price-card">
                              <div className="price-row">
                                <div className="price-left">
                                  <span className="price-label-text">{t('price')}</span>
                                  <div className="price-amount">
                                    {painting.price ? (
                                        <>
                                          <span className="price-value">{painting.price}</span>
                                          <span className="price-currency">{t('euro')}</span>
                                        </>
                                    ) : (
                                        <span className="price-contact">Contact for price</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3">
                                <DoartiCta
                                  href={painting.externalBuyUrl}
                                  className="buy-action-btn w-100"
                                  icon="bi-box-arrow-up-right"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('buy_external')}
                                </DoartiCta>
                              </div>
                            </div>
                        ) : (
                            <div className="price-card">
                              <div className="price-row">
                                <div className="price-left">
                                  <div className="price-left__main">
                                    <span className="price-label-text">{t('price')}</span>
                                    <div className="price-amount">
                                      {painting.price ? (
                                          <>
                                            <span className="price-value">{painting.price}</span>
                                            <span className="price-currency">{t('euro')}</span>
                                          </>
                                      ) : (
                                          <span className="price-contact">Contact for price</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {painting.price && (
                                  <div className="price-right">
                                    <BuyButton
                                      productType="original"
                                      productId={`${year}/${slug}`}
                                      title={getPaintingTitle()}
                                      priceEur={painting.price}
                                      imagePath={`/${painting.image}`}
                                      className="buy-action-wrap"
                                    />
                                  </div>
                                )}
                              </div>
                              <ShippingInfo />
                            </div>
                        )}
                        {matchingPrint && (
                          <div className="mt-3">
                            <Link to={`/prints/${matchingPrint.slug}`} className="btn buy-print-btn w-100">
                              {t('prints.buy_print')}
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* You can also like section */}
        {relatedPaintings.length > 0 && (
          <section className="related-paintings-section section-padding">
            <div className="container">
              <div className="page-heading mb-4">
                <h2 className="page-heading__title">{t('you_may_also_like')}</h2>
              </div>
              <div className="gallery-grid">
                {relatedPaintings.map((relatedPainting) => {
                  const paintingSlug = relatedPainting.link.split('/').pop();
                  const paintingTitle = t(`${paintingSlug}_heading`) || relatedPainting.title;
                  const paintingDescription = truncateText(
                    t(`${paintingSlug}_description`) || relatedPainting.description,
                    100
                  );

                  return (
                    <Link
                      key={relatedPainting.link}
                      to={`/painting/${relatedPainting.link}`}
                      className="gallery-card"
                      aria-label={paintingTitle}
                    >
                      <div className="gallery-card__frame">
                        {relatedPainting.sold ? (
                          <span className="status-tag status-tag--sold status-tag--compact">
                            {t('sold')}
                          </span>
                        ) : (
                          <span className="status-tag status-tag--available status-tag--compact">
                            {t('available')}
                          </span>
                        )}
                        <ImageLoader
                          src={`/${relatedPainting.image}`}
                          alt={paintingTitle}
                          className="img-fluid"
                        />
                        <div className="gallery-card__overlay">
                          <h3 className="gallery-card__title">{paintingTitle}</h3>
                          <div className="gallery-card__tags">
                            <span className="gallery-card__tag">{t('oil_painting')}</span>
                            <span className="gallery-card__tag">{relatedPainting.dimensions}</span>
                          </div>
                          {paintingDescription ? (
                            <p className="gallery-card__desc">{paintingDescription}</p>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

      {lightboxOpen &&
        createPortal(
          <div
            className="painting-lightbox"
            onClick={handleCloseLightbox}
            role="dialog"
            aria-modal="true"
            aria-label={getPaintingTitle()}
          >
            <div
              className="painting-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="painting-lightbox-image-container">
                <img
                  src={`/${painting.image}`}
                  alt={getPaintingTitle()}
                  className="painting-lightbox-image"
                  style={{ transform: `scale(${zoomLevel / 100})` }}
                />
              </div>

              <div className="painting-lightbox-bar">
                <div className="painting-lightbox-zoom-controls">
                  <button
                    type="button"
                    className="painting-zoom-btn"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 50}
                    aria-label="Zoom out"
                  >
                    <i className="bi bi-zoom-out" aria-hidden="true" />
                  </button>
                  <span className="painting-zoom-level">{zoomLevel}%</span>
                  <button
                    type="button"
                    className="painting-zoom-btn"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 300}
                    aria-label="Zoom in"
                  >
                    <i className="bi bi-zoom-in" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="painting-zoom-btn"
                    onClick={handleResetZoom}
                    aria-label="Reset zoom"
                  >
                    <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                  </button>
                </div>

                <button
                  type="button"
                  className="painting-lightbox-close"
                  onClick={handleCloseLightbox}
                  aria-label="Close lightbox"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default PaintingDetail;
